import { View } from "react-native";
import React, { useState } from "react";
import {
  AccountMemberDetails,
  BillingInterval,
  BuyFlowUser,
  PlanPackageDetails,
  PrimaryUser,
} from "features/types";
import { useAppDispatch } from "hooks/useAppDispatch";
import { setSecondaryUsers, setStep } from "features/slices/buyFlowSlice";
import { useAppSelector } from "hooks/useAppSelector";
import UserDetails from "./components/UserDetails";
import EditMemberModal from "./components/EditMemberModal";
import toast from "utils/toast";
import { emailEqual } from "./registerUtils";
import Button from "components/Button";
import BuyFlowPaymentDetailCard from "components/BuyFlowPaymentDetailCard";
import i18n from "plugins/localization";
import userServices from "services/user.services";
import { useMutation } from "@tanstack/react-query";
import { trackConversion } from "queries/conversion.queries";

interface StepTwoProps {
  selectedBillingInterval: BillingInterval;
  selectedPlanPackage: PlanPackageDetails | null;
  onContinuePress: () => void;
}

export const userEmailUsedByOtherUsers = (
  email: string,
  secondaryUsers: BuyFlowUser[],
  editingUser: BuyFlowUser,
  primaryMember: AccountMemberDetails | null, // used in add membership
  primaryUser: PrimaryUser | null // used in registration flow
) => {
  if (emailEqual(email, editingUser.email)) {
    return false;
  }
  if (primaryMember && emailEqual(email, primaryMember.user.email)) {
    return true;
  }
  if (primaryUser && emailEqual(email, primaryUser.email)) {
    return true;
  }
  return secondaryUsers.some((user) => emailEqual(email, user.email));
};

const StepTwo: React.FC<StepTwoProps> = ({
  selectedPlanPackage,
  onContinuePress,
}) => {
  const dispatch = useAppDispatch();
  const {
    secondaryUsers,
    editingUser,
    primaryUser,
    signupCharge,
    shouldCharge,
    step,
  } = useAppSelector((state) => state.buyFlow);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingMemberIndex, setEditingMemberIndex] = useState(0);
  const { mutate: track } = useMutation(trackConversion);

  if (!selectedPlanPackage) return null;

  const handleNewMemberSubmit = async (user: BuyFlowUser) => {
    // check if new user email exists in system
    if (user.minor === false) {
      const userExisted = await userServices.userExists(user.email!);
      if (userExisted) {
        toast.error("This email already exists.");
        return;
      }

      // check if new user email conficts with others
      if (
        userEmailUsedByOtherUsers(
          user.email!,
          secondaryUsers,
          editingUser,
          null,
          primaryUser
        )
      ) {
        toast.error(
          i18n.t("emailUsedByOtherMemberError", { email: user.email })
        );
        return;
      }
    }
    // check if adding or editing
    if (editingMemberIndex < 0) {
      // adding
      dispatch(setSecondaryUsers(secondaryUsers.concat(user)));
    } else {
      // editing
      const newSecondaryUsers = [...secondaryUsers];
      newSecondaryUsers[editingMemberIndex] = user;
      dispatch(setSecondaryUsers(newSecondaryUsers));
    }
    setModalVisible(false);
  };

  const determineNextStep = () => {
    if (shouldCharge) {
      // plan package is not free
      track({ step: "ADDITIONAL_MEMBERS_INFO_ENTERED", flow: "REGISTER" });
      dispatch(setStep(step + 1));
    } else {
      track({ step: "PURCHASE_COMPLETE", flow: "REGISTER" });
      onContinuePress();
    }
  };
  return (
    <View>
      <View style={{ marginBottom: 45 }}>
        <UserDetails
          setVisible={setModalVisible}
          setEditingMemberIndex={setEditingMemberIndex}
        />
      </View>
      <View style={{ marginBottom: 35 }}>
        {selectedPlanPackage && signupCharge !== null && (
          <BuyFlowPaymentDetailCard
            planPackage={selectedPlanPackage}
            signupCharge={signupCharge}
          ></BuyFlowPaymentDetailCard>
        )}
      </View>
      <View>
        <Button onPress={determineNextStep} testID="step-two-continue-button">
          {i18n.t("common.continue")}
        </Button>
      </View>
      <EditMemberModal
        visible={modalVisible}
        setVisible={setModalVisible}
        handleNewMemberSubmit={handleNewMemberSubmit}
      />
    </View>
  );
};

export default StepTwo;
